<template>
    <div class="content-wrapper">
        <div class="row">
            <div class="col-sm-12 col-md-6">
                <app-breadcrumb :page-title="$t('incident_reporting')" :icon="'shield'" />
            </div>
            <div v-if="$can('create_incidents')" class="col-sm-12 col-md-6 breadcrumb-side-button">
                <div class="float-md-right mb-3 mb-sm-3 mb-md-0">
                    <button type="button" class="btn btn-primary btn-with-shadow" @click="openAddEditModal">
                        {{ $t('report_incident') }}
                    </button>
                </div>
            </div>
        </div>

        <app-table :id="tableId" :options="options" @action="getListAction" />

        <incident-add-edit-modal v-if="isAddEditModalActive"
                                 :table-id="tableId"
                                 :selected-url="selectedUrl"
                                 @close-modal="closeAddEditModal" />

        <incident-report-preview-drawer
            v-if="isReportDrawerActive && selectedIncidentId"
            :incident-id="selectedIncidentId"
            @close-modal="closeReportDrawer"
        />

        <app-delete-modal v-if="deleteConfirmationModalActive"
                          :preloader="deleteLoader"
                          modal-id="incident-delete"
                          @confirmed="confirmed"
                          @cancelled="cancelled" />
    </div>
</template>

<script>
import moment from 'moment';
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import { formatted_date } from '../../../Helpers/DateTimeHelper';
import * as actions from "../../../Config/ApiUrl";

export default {
    extends: CoreLibrary,
    name: "AppIncidents",
    data() {
        return {
            isAddEditModalActive: false,
            isReportDrawerActive: false,
            deleteConfirmationModalActive: false,
            deleteLoader: false,
            selectedUrl: '',
            selectedIncidentId: null,
            tableId: 'incidents-table',
            rowData: {},
            categoryOptions: [
                { id: 'hse', name: this.$t('incident_category_hse') },
                { id: 'security', name: this.$t('incident_category_security') },
                { id: 'operations', name: this.$t('incident_category_operations') },
            ],
            severityOptions: [
                { id: 'low', name: this.$t('low') },
                { id: 'medium', name: this.$t('medium') },
                { id: 'high', name: this.$t('high') },
                { id: 'critical', name: this.$t('critical') },
            ],
            statusOptions: [
                { id: 'reported', name: this.$t('reported') },
                { id: 'acknowledged', name: this.$t('acknowledged') },
                { id: 'in_progress', name: this.$t('in_progress') },
                { id: 'resolved', name: this.$t('resolved') },
                { id: 'closed', name: this.$t('closed') },
            ],
            typeOptions: [
                { id: 'road_traffic_accident', name: this.$t('road_traffic_accident') },
                { id: 'people_safety_incident', name: this.$t('people_safety_incident') },
                { id: 'driver_intoxication', name: this.$t('driver_intoxication') },
                { id: 'sop_non_compliance', name: this.$t('sop_non_compliance') },
                { id: 'near_miss', name: this.$t('near_miss') },
                { id: 'theft_attempt', name: this.$t('theft_attempt') },
                { id: 'hijacking_attack', name: this.$t('hijacking_attack') },
                { id: 'cargo_loss', name: this.$t('cargo_loss') },
                { id: 'external_intrusion', name: this.$t('external_intrusion') },
                { id: 'mechanical_breakdown', name: this.$t('mechanical_breakdown') },
                { id: 'fuel_shortage', name: this.$t('fuel_shortage') },
                { id: 'toll_cash_shortage', name: this.$t('toll_cash_shortage') },
                { id: 'operational_delay', name: this.$t('operational_delay') },
                { id: 'documentation_issue', name: this.$t('documentation_issue') },
            ],
        }
    },
    computed: {
        options() {
            return {
                name: 'AppIncidents',
                url: actions.INCIDENT,
                showHeader: true,
                enableHighlights: true,
                enableRowSelect: false,
                enableSaveFilter: true,
                columns: [
                    {
                        title: this.$t('code'),
                        type: 'custom-html',
                        key: 'incident_code',
                        modifier: (value) => value ? `<span class="incident-code-pill">${value}</span>` : '---',
                    },
                    {
                        title: this.$t('lot_number'),
                        type: 'component',
                        key: 'trip_id',
                        componentName: 'incident-trip-live-tracking-cell',
                    },
                    // {
                    //     title: this.$t('incident_title'),
                    //     type: 'text',
                    //     key: 'name',
                    // },
                    {
                        title: this.$t('category'),
                        type: 'custom-html',
                        key: 'category',
                        modifier: (value) => `<span>${this.translateCategory(value)}</span>`,
                    },
                    {
                        title: this.$t('incident_type'),
                        type: 'custom-html',
                        key: 'type',
                        modifier: (value) => `<span>${this.translateIncidentType(value)}</span>`,
                    },
                    {
                        title: this.$t('confirmed_material_loss'),
                        type: 'custom-html',
                        key: 'material_loss_status',
                        modifier: (value, row) => {
                            const materialLossStatus = this.resolveMaterialLossStatus(value, row);

                            if (!materialLossStatus) {
                                return '---';
                            }

                            const badgeMap = {
                                yes: 'danger',
                                no: 'success',
                                under_investigation: 'warning',
                            };
                            const label = materialLossStatus === 'under_investigation'
                                ? this.$t('under_investigation')
                                : this.$t(materialLossStatus);

                            return `<span class="badge badge-pill badge-${badgeMap[materialLossStatus] || 'secondary'}">${label}</span>`;
                        }
                    },
                    {
                        title: this.$t('severity'),
                        type: 'custom-html',
                        key: 'severity',
                        modifier: (value) => {
                            const colorMap = {
                                low: 'secondary',
                                medium: 'warning',
                                high: 'danger',
                                critical: 'dark',
                            };

                            return `<span class="badge badge-pill badge-${colorMap[value] || 'secondary'}">${this.$t(value)}</span>`;
                        }
                    },
                    {
                        title: this.$t('status'),
                        type: 'custom-html',
                        key: 'status',
                        modifier: (value) => {
                            const colorMap = {
                                reported: 'warning',
                                acknowledged: 'info',
                                in_progress: 'primary',
                                resolved: 'success',
                                closed: 'secondary',
                            };

                            return `<span class="badge badge-pill badge-${colorMap[value] || 'secondary'}">${this.$t(value)}</span>`;
                        }
                    },
                    {
                        title: this.$t('credit_note'),
                        type: 'custom-html',
                        key: 'credit_note_generated',
                        modifier: (value, row) => {
                            if (this.resolveMaterialLossStatus(row.material_loss_status, row) !== 'yes') {
                                return '---';
                            }

                            const isGenerated = value === true || value === 1 || value === '1';
                            const badgeClass = isGenerated ? 'success' : 'secondary';
                            const label = isGenerated ? this.$t('yes') : this.$t('no');

                            return `<span class="badge badge-pill badge-${badgeClass}">${label}</span>`;
                        }
                    },
                    // {
                    //     title: this.$t('tracker'),
                    //     type: 'text',
                    //     key: 'device_imei',
                    // },
                    {
                        title: this.$t('reported_at'),
                        type: 'custom-html',
                        key: 'reported_at',
                        modifier: (value) => `<span>${this.formatIncidentDate(value)}</span>`,
                    },
                    {
                        title: this.$t('documents'),
                        type: 'custom-html',
                        key: 'police_report_path',
                        modifier: (value, row) => {
                            const docs = [];

                            if (value) {
                                docs.push(this.$t('police_report'));
                            }

                            if (row.internal_incident_report_path) {
                                docs.push(this.$t('internal_incident_report'));
                            }

                            return docs.length ? docs.join(' / ') : '---';
                        }
                    },
                    {
                        title: this.$t('actions'),
                        type: 'action'
                    }
                ],
                actions: [
                    {
                        title: this.$t('view_report'),
                        icon: 'file-text',
                        type: 'none',
                    },
                    {
                        title: this.$t('edit'),
                        icon: 'edit',
                        type: 'none',
                        modalId: 'incident-add-edit-modal',
                        modifier: () => this.$can('update_incidents'),
                    },
                    {
                        title: this.$t('delete'),
                        icon: 'trash',
                        type: 'none',
                        modalId: 'incident-delete',
                        modifier: () => this.$can('delete_incidents'),
                    }
                ],
                filters: [
                    {
                        title: this.$t('category'),
                        type: 'multi-select-filter',
                        key: 'category',
                        option: this.categoryOptions,
                        listValueField: 'name'
                    },
                    {
                        title: this.$t('incident_type'),
                        type: 'multi-select-filter',
                        key: 'type',
                        option: this.typeOptions,
                        listValueField: 'name'
                    },
                    {
                        title: this.$t('severity'),
                        type: 'multi-select-filter',
                        key: 'severity',
                        option: this.severityOptions,
                        listValueField: 'name'
                    },
                    {
                        title: this.$t('status'),
                        type: 'multi-select-filter',
                        key: 'status',
                        option: this.statusOptions,
                        listValueField: 'name'
                    },
                    {
                        title: this.$t('reported_at'),
                        type: 'range-picker',
                        key: 'reported_at',
                        option: ['today', 'thisMonth', 'lastMonth', 'thisYear', 'lastYear']
                    },
                    {
                        title: this.$t('occurred_at'),
                        type: 'range-picker',
                        key: 'occurred_at',
                        option: ['today', 'thisMonth', 'lastMonth', 'thisYear', 'lastYear']
                    },
                ],
                showFilter: true,
                showSearch: true,
                showClearFilter: true,
                paginationType: "pagination",
                responsive: true,
                rowLimit: 8,
                showAction: true,
                orderBy: 'desc',
                actionType: "default",
            };
        }
    },
    methods: {
        translateCategory(value) {
            const keyMap = {
                hse: 'incident_category_hse',
                security: 'incident_category_security',
                operations: 'incident_category_operations',
            };

            return value ? this.$t(keyMap[value] || value) : '---';
        },
        translateIncidentType(value) {
            return value ? this.$t(value) : '---';
        },
        resolveMaterialLossStatus(value, row = {}) {
            if (value) {
                return value;
            }

            if (row.credit_note_generated || row.credit_note_reference || row.credit_note_generated_at || row.type === 'cargo_loss') {
                return 'yes';
            }

            return null;
        },
        formatIncidentDate(value) {
            if (!value) {
                return '---';
            }

            const dateFormat = formatted_date() || 'DD-MM-YYYY';
            const timeFormat = window.settings?.time_format === 'h' ? 'hh:mm A' : 'HH:mm';

            return moment(value).format(`${dateFormat} ${timeFormat}`);
        },
        openAddEditModal() {
            this.isAddEditModalActive = true;
        },
        closeAddEditModal() {
            $("#incident-add-edit-modal").modal('hide');
            this.isAddEditModalActive = false;
            this.reSetData();
        },
        getListAction(rowData, actionObj) {
            this.rowData = rowData;

            if (actionObj.title === this.$t('delete')) {
                this.openDeleteModal();
            } else if (actionObj.title === this.$t('view_report')) {
                this.selectedIncidentId = rowData.id;
                this.isReportDrawerActive = true;
            } else if (actionObj.title === this.$t('edit')) {
                this.selectedUrl = `${actions.INCIDENT}/${rowData.id}`;
                this.openAddEditModal();
            }
        },
        closeReportDrawer() {
            this.isReportDrawerActive = false;
            this.selectedIncidentId = null;
        },
        openDeleteModal() {
            this.deleteConfirmationModalActive = true;
        },
        confirmed() {
            const url = `${actions.INCIDENT}/${this.rowData.id}`;
            this.deleteLoader = true;

            this.axiosDelete(url)
                .then((response) => {
                    this.deleteLoader = false;
                    $("#incident-delete").modal('hide');
                    this.cancelled();
                    this.$toastr.s(response.data.message);
                }).finally(() => {
                    this.$hub.$emit(`reload-${this.tableId}`);
                });
        },
        cancelled() {
            this.deleteConfirmationModalActive = false;
            this.reSetData();
        },
        reSetData() {
            this.rowData = {};
            this.selectedUrl = '';
            this.selectedIncidentId = null;
        },
    },
}
</script>

<style scoped>
.incident-code-pill {
    display: inline-flex;
    align-items: center;
    padding: 0.24rem 0.55rem;
    border-radius: 999px;
    background: rgba(var(--brand-primary-rgb), 0.08);
    color: var(--brand-primary);
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    white-space: nowrap;
}

.incident-trip-cell {
    display: inline-flex;
    flex-direction: column;
    line-height: 1.35;
}

.incident-trip-primary {
    font-weight: 600;
}

.incident-trip-secondary {
    font-size: 0.78rem;
    color: var(--muted-font-color);
}
</style>
